import { hours, loadPersonToil, type ToilEvent } from '@/data/toil'
import { useAsync } from '@/data/useAsync'
import { Card, Failed, Loading, Tile } from '@/components/bits'
import { shortDate, timeOfDay } from '@/lib/format'
import type { PersonDetail } from '@/data/queries'
import { useOrg } from '@/org/OrgProvider'

/**
 * One person's TOIL, under their profile.
 *
 * TWO NUMBERS, NEVER ADDED. This is the whole design of the tab.
 *
 *   Authorised here    what TFH-HR authorised from shift extensions since
 *                      go-live. Complete, provable, and frozen per event at
 *                      the moment of signing.
 *   Planday's balance  what the person's TOIL account holds. Cumulative since
 *                      2021, maintained by hand, and on HOTEL1 seven of
 *                      sixteen non-zero balances are NEGATIVE.
 *
 * They are different quantities with different histories, and adding them
 * would produce a figure that is true of nothing. The gap between them is
 * shown as its own line, labelled as a comparison rather than an error,
 * because a difference here is expected: Planday's number includes years this
 * system never saw.
 */

function statusLine(ev: ToilEvent): string {
  switch (ev.status) {
    case 'observed':
      return ev.payBasis === 'unclear' ? 'waiting on HR to settle their pay basis' : 'waiting on their head of department'
    case 'hod_approved':
      return 'approved, waiting to be authorised'
    case 'authorised':
      return `authorised ${hours(ev.authorisedHours)}`
    case 'rejected':
      return 'refused'
    case 'not_eligible':
      return 'paid rather than banked, as they are paid hourly'
  }
}

export function Toil({ person }: { person: PersonDetail }) {
  const { current } = useOrg()
  const personId = person.person.id
  const state = useAsync(() => loadPersonToil(current.id, personId), [current.id, personId])

  if (state.state === 'loading') return <Loading what="TOIL" />
  if (state.state === 'error') return <Failed message={state.message} onRetry={state.reload} />

  const { balance, events } = state.data

  if (balance === null) {
    return (
      <Card title="TOIL">
        <p>
          Nothing yet. No shift extension has been recorded for{' '}
          {person.person.preferred_name ?? person.person.first_name} since TOIL was
          switched on, and Planday&rsquo;s TOIL account has not been read for them.
        </p>
      </Card>
    )
  }

  const gap = balance.plandayLessAuthorised

  return (
    <>
      <div className="tiles">
        <Tile label="Authorised here" value={hours(balance.authorisedHours)} />
        <Tile
          label="Planday's TOIL account"
          value={balance.plandayBalanceHours === null ? 'not read' : hours(balance.plandayBalanceHours)}
        />
        <Tile label="Waiting on their HOD" value={hours(balance.awaitingHodHours)} />
        <Tile label="Waiting to be authorised" value={hours(balance.awaitingAuthorisationHours)} />
      </div>

      <Card title="The two figures">
        <p>
          <strong>{hours(balance.authorisedHours)}</strong> across{' '}
          {balance.authorisedEvents === 1 ? '1 extension' : `${balance.authorisedEvents} extensions`}{' '}
          has been authorised in this system since TOIL went live. That figure is frozen per
          extension at the moment it was signed off, so editing a shift in Planday afterwards cannot
          change it.
        </p>

        {balance.plandayBalanceHours === null ? (
          <p className="muted">
            Planday&rsquo;s own TOIL balance has not been read for this person. Either they hold no
            active TOIL account, or the balance reader has not run since they were added.
          </p>
        ) : (
          <>
            <p>
              Planday&rsquo;s TOIL account holds{' '}
              <strong>{hours(balance.plandayBalanceHours)}</strong>
              {balance.plandayAccountStatus !== null && balance.plandayAccountStatus !== 'Active' && (
                <> on an account marked {balance.plandayAccountStatus.toLowerCase()}</>
              )}
              , read{' '}
              {balance.plandayReadAt === null
                ? 'at an unknown time'
                : `${timeOfDay(balance.plandayReadAt)} on ${shortDate(balance.plandayReadAt)}`}
              . That balance runs back to 2021 and is maintained by hand in Planday, so it is not the
              same quantity as the figure above and the two are never added together.
            </p>

            {balance.plandayBalanceHours < 0 && (
              <p className="callout">
                This balance is negative, which means more TOIL has been taken than earned. It is
                shown as it is rather than as zero.
              </p>
            )}

            {gap !== null && Math.abs(gap) >= 0.01 && (
              <p className="muted">
                Planday is {hours(Math.abs(gap))} {gap > 0 ? 'higher' : 'lower'} than what this
                system has authorised. A difference is expected: Planday&rsquo;s figure includes
                years this system never saw, and hours taken as time off, neither of which appear
                above.
              </p>
            )}
          </>
        )}
      </Card>

      <Card title={`Extensions (${events.length})`}>
        {events.length === 0 && <p className="muted">No shift extensions recorded since go-live.</p>}
        {events.map((ev) => (
          <div className="stackfield" key={ev.id}>
            <div>
              <strong>{shortDate(ev.shiftDate)}</strong>
              <span className="muted">
                {' '}
                · {hours(ev.hours)} · {statusLine(ev)}
              </span>
            </div>
            {ev.hodReason !== null && (
              <p>
                <span className="muted">Reason: </span>
                {ev.hodReason}
              </p>
            )}
            {ev.hoursChangedSinceAuthorisation && (
              <p className="callout">
                Planday now reads {hours(ev.hours)} for this shift, against {hours(ev.authorisedHours)}{' '}
                authorised. The authorised figure is what counts.
              </p>
            )}
            {ev.status === 'authorised' &&
              (ev.creditStatus === 'pending' || ev.creditStatus === 'blocked') && (
                <p className="muted">
                  No matching credit has been found on their Planday TOIL account yet. Planday
                  usually adds one itself when the shift becomes TOIL Worked.
                </p>
              )}
          </div>
        ))}
      </Card>
    </>
  )
}
